import React, { useState, useEffect, useCallback } from 'react';
import {
  FiUser, FiEdit2, FiSave, FiX, FiTrash2, FiDownloadCloud, FiUpload,
  FiMapPin, FiGlobe, FiAward, FiBriefcase, FiUsers, FiZap,
  FiFeather, FiTrendingUp, FiAlertCircle, FiCheckCircle, FiRefreshCw,
  FiChevronDown, FiChevronRight,
} from 'react-icons/fi';
import { getAuthHeaders } from '../utils/auth';
import { getApiEndpoint } from '../utils/api';
import ConsentModal from '../components/ConsentModal';
import './PIProfile.css';

const CAREER_LABELS = {
  esi: 'Early Stage Investigator',
  established: 'Established PI',
};

const PIProfile = ({ user }) => {
  const [profile, setProfile] = useState(null);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Edit state
  const [editingSection, setEditingSection] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [fetchingGrants, setFetchingGrants] = useState(false);
  const [message, setMessage] = useState(null);
  const [seedUrl, setSeedUrl] = useState('');
  const [seedName, setSeedName] = useState('');
  const [seeding, setSeeding] = useState(false);
  const [uploadingProposal, setUploadingProposal] = useState(false);
  const [uploadedProposals, setUploadedProposals] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [expandedProposal, setExpandedProposal] = useState(null);
  const [editingTitle, setEditingTitle] = useState(null);
  const [editTitleValue, setEditTitleValue] = useState('');
  const [uploadForm, setUploadForm] = useState({
    files: [], outcome: 'funded', score: '', feedback: '', feedbackFile: null, nofoUrl: '', nofoFile: null,
    contributeToCorpus: false,
  });
  const [consentStatus, setConsentStatus] = useState(user?.data_consent_status || 'none');
  const [showConsentModal, setShowConsentModal] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [profRes, partRes, uplRes] = await Promise.all([
        fetch(getApiEndpoint('/api/grant-proposals/pi-profile'), { headers: getAuthHeaders() }),
        fetch(getApiEndpoint('/api/grant-proposals/partner-profiles'), { headers: getAuthHeaders() }),
        fetch(getApiEndpoint('/api/grant-proposals/uploaded-proposals'), { headers: getAuthHeaders() }),
      ]);
      if (profRes.ok) setProfile(await profRes.json());
      if (partRes.ok) setPartners(await partRes.json());
      if (uplRes.ok) setUploadedProposals(await uplRes.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const startEdit = (section) => {
    setEditingSection(section);
    setForm({ ...profile });
    setMessage(null);
  };

  const cancelEdit = () => {
    setEditingSection(null);
    setForm({});
  };

  const saveProfile = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const res = await fetch(getApiEndpoint('/api/grant-proposals/pi-profile'), {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          data: {
            pi_name: form.pi_name,
            institution: form.institution,
            expertise: form.expertise,
            prior_grants: form.prior_grants,
            team_assets: form.team_assets,
            homepage_url: form.homepage_url,
            career_stage: form.career_stage,
          },
        }),
      });
      if (!res.ok) throw new Error('Save failed');
      const updated = await res.json();
      setProfile(prev => ({ ...prev, ...updated }));
      setEditingSection(null);
      setMessage({ type: 'success', text: 'Profile saved' });
      setTimeout(() => setMessage(null), 3000);
    } catch (e) {
      setMessage({ type: 'error', text: e.message });
    } finally {
      setSaving(false);
    }
  };

  const fetchGrants = async () => {
    setFetchingGrants(true);
    setMessage(null);
    try {
      const params = new URLSearchParams();
      if (form.pi_name) params.set('name', form.pi_name);
      if (form.institution) params.set('institution', form.institution);
      if (form.homepage_url) {
        const orcidMatch = form.homepage_url.match(/orcid\.org\/(\d{4}-\d{4}-\d{4}-\d{3}[\dX])/i);
        if (orcidMatch) params.set('orcid', orcidMatch[1]);
      }
      const res = await fetch(getApiEndpoint(`/api/grant-proposals/fetch-pi-grants?${params}`), {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error('Fetch failed');
      const data = await res.json();
      if (data.grants_text) {
        setForm(prev => ({
          ...prev,
          prior_grants: prev.prior_grants
            ? `${prev.prior_grants}\n\n${data.grants_text}`
            : data.grants_text,
        }));
        setMessage({ type: 'success', text: 'Grants fetched — review and save' });
      } else {
        setMessage({ type: 'info', text: 'No grants found for this name/institution' });
      }
    } catch (e) {
      setMessage({ type: 'error', text: e.message });
    } finally {
      setFetchingGrants(false);
    }
  };

  const deletePartner = async (id) => {
    try {
      const res = await fetch(getApiEndpoint(`/api/grant-proposals/partner-profiles/${id}`), {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        setPartners(prev => prev.filter(p => p.id !== id));
      }
    } catch (_) {}
  };

  const seedFromUrl = async () => {
    if (!seedUrl.trim()) return;
    setSeeding(true);
    setMessage(null);
    try {
      const res = await fetch(getApiEndpoint('/api/grant-proposals/seed-profile'), {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: seedUrl.trim(), name: seedName.trim() || null }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to seed profile');
      }
      const data = await res.json();
      await fetchData();
      setSeedUrl('');
      setSeedName('');
      const parts = [];
      if (data.profile?.pi_name) parts.push(data.profile.pi_name);
      if (data.paper_count) parts.push(`${data.paper_count} papers imported`);
      if (data.sources_used?.length) parts.push(`sources: ${data.sources_used.join(', ')}`);
      const logStr = data.log?.length ? ` [${data.log.join(' → ')}]` : '';
      setMessage({
        type: parts.length > 0 ? 'success' : 'info',
        text: parts.length > 0
          ? `Profile seeded — ${parts.join(', ')}${logStr}`
          : `No data found.${logStr}`,
      });
    } catch (e) {
      setMessage({ type: 'error', text: e.message });
    } finally {
      setSeeding(false);
    }
  };

  const submitProposalUpload = async () => {
    if (!uploadForm.files.length) return;
    setUploadingProposal(true);
    setMessage(null);
    try {
      const formData = new FormData();
      uploadForm.files.forEach(f => formData.append('files', f));
      formData.append('funding_outcome', uploadForm.outcome);
      if (uploadForm.score) formData.append('priority_score', uploadForm.score);
      if (uploadForm.feedback) formData.append('reviewer_feedback', uploadForm.feedback);
      if (uploadForm.feedbackFile) formData.append('feedback_file', uploadForm.feedbackFile);
      if (uploadForm.nofoUrl) formData.append('nofo_url', uploadForm.nofoUrl);
      if (uploadForm.nofoFile) formData.append('nofo_file', uploadForm.nofoFile);
      if (uploadForm.contributeToCorpus && consentStatus === 'granted') {
        formData.append('contribute_to_corpus', 'true');
      }
      const res = await fetch(getApiEndpoint('/api/grant-proposals/upload-winning-proposal'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Upload failed');
      }
      const data = await res.json();
      await fetchData();
      setShowUploadModal(false);
      setUploadForm({ files: [], outcome: 'funded', score: '', feedback: '', feedbackFile: null, nofoUrl: '', nofoFile: null, contributeToCorpus: false });
      const extras = [
        data.has_nofo && 'NOFO attached',
        data.has_feedback && 'reviewer feedback captured',
        data.priority_score && `score: ${data.priority_score}`,
      ].filter(Boolean);
      setMessage({
        type: 'success',
        text: `Imported "${data.title}" (${data.funding_outcome}) — ${data.sections?.length || 0} sections parsed.${extras.length ? ' ' + extras.join(', ') + '.' : ''} Style fingerprint and win patterns will update shortly.`,
      });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setUploadingProposal(false);
    }
  };

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  if (loading) {
    return (
      <div className="pp-loading">
        <div className="spinner" />
        <p>Loading profile...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pp-error">
        <FiAlertCircle /> {error}
        <button onClick={fetchData}><FiRefreshCw /> Retry</button>
      </div>
    );
  }

  const p = profile || {};
  const grantHistory = p.grant_history || [];
  const styleFp = p.style_fingerprint;
  const winPatterns = p.win_patterns;
  const critiques = p.recurring_reviewer_critiques || [];

  return (
    <div className="pp-page">

      {message && (
        <div className={`pp-message pp-message--${message.type}`}>
          {message.type === 'success' && <FiCheckCircle />}
          {message.type === 'error' && <FiAlertCircle />}
          {message.text}
        </div>
      )}

      {/* ── Seed from URL ─────────────────────────────────────────────── */}
      {!p.pi_name ? (
        <div className="pp-seed-banner">
          <div className="pp-seed-text">
            <h3><FiGlobe /> Seed your profile from your website</h3>
            <p>Paste your faculty page or ORCID URL. Add your name if the page is dynamically loaded. We'll import your publications, detect your institution, and fetch your funded grants.</p>
          </div>
          <div className="pp-seed-form">
            <input
              className="pp-seed-input"
              value={seedUrl}
              onChange={e => setSeedUrl(e.target.value)}
              placeholder="https://faculty.university.edu/yourname or https://orcid.org/0000-..."
              disabled={seeding}
            />
            <input
              className="pp-seed-input pp-seed-input--name"
              value={seedName}
              onChange={e => setSeedName(e.target.value)}
              placeholder="Your full name (helps if page is dynamically loaded)"
              disabled={seeding}
              onKeyDown={e => e.key === 'Enter' && seedFromUrl()}
            />
            <button className="pp-btn pp-btn--primary" onClick={seedFromUrl} disabled={seeding || (!seedUrl.trim() && !seedName.trim())}>
              <FiDownloadCloud /> {seeding ? 'Importing...' : 'Import'}
            </button>
          </div>
        </div>
      ) : (
        <div className="pp-seed-compact">
          <FiGlobe />
          <input
            className="pp-seed-input-sm"
            value={seedUrl}
            onChange={e => setSeedUrl(e.target.value)}
            placeholder="Paste a website URL to re-seed profile"
            disabled={seeding}
            onKeyDown={e => e.key === 'Enter' && seedFromUrl()}
          />
          <button className="pp-btn pp-btn--outline pp-btn--sm" onClick={seedFromUrl} disabled={seeding || !seedUrl.trim()}>
            {seeding ? 'Importing...' : 'Import'}
          </button>
        </div>
      )}

      {/* ── A. Identity Header ─────────────────────────────────────────── */}
      <div className="pp-section pp-identity">
        <div className="pp-section-header">
          <h2><FiUser /> Research Identity</h2>
          {editingSection !== 'identity' && (
            <button className="pp-edit-btn" onClick={() => startEdit('identity')}>
              <FiEdit2 /> Edit
            </button>
          )}
        </div>

        {editingSection === 'identity' ? (
          <div className="pp-form">
            <div className="pp-form-row">
              <div className="pp-form-group">
                <label>Full Name *</label>
                <input value={form.pi_name || ''} onChange={e => set('pi_name', e.target.value)} placeholder="Dr. Jane Smith" />
              </div>
              <div className="pp-form-group">
                <label>Institution *</label>
                <input value={form.institution || ''} onChange={e => set('institution', e.target.value)} placeholder="University of Utah" />
              </div>
            </div>
            <div className="pp-form-row">
              <div className="pp-form-group">
                <label>Website / ORCID</label>
                <input value={form.homepage_url || ''} onChange={e => set('homepage_url', e.target.value)} placeholder="https://orcid.org/0000-..." />
              </div>
              <div className="pp-form-group">
                <label>Career Stage</label>
                <select value={form.career_stage || ''} onChange={e => set('career_stage', e.target.value)}>
                  <option value="">Select...</option>
                  <option value="esi">Early Stage Investigator (ESI)</option>
                  <option value="established">Established PI</option>
                </select>
              </div>
            </div>
            <div className="pp-save-row">
              <button className="pp-btn pp-btn--primary" onClick={saveProfile} disabled={saving}>
                <FiSave /> {saving ? 'Saving...' : 'Save'}
              </button>
              <button className="pp-btn pp-btn--outline" onClick={cancelEdit}>
                <FiX /> Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="pp-identity-display">
            {p.pi_name ? (
              <>
                <h3 className="pp-identity-name">{p.pi_name}</h3>
                {p.institution && <p className="pp-identity-inst"><FiMapPin /> {p.institution}</p>}
                {p.career_stage && (
                  <span className={`pp-career-chip pp-career-chip--${p.career_stage}`}>
                    {CAREER_LABELS[p.career_stage] || p.career_stage}
                  </span>
                )}
                {p.homepage_url && (
                  <a className="pp-homepage-link" href={p.homepage_url} target="_blank" rel="noreferrer">
                    <FiGlobe /> {p.homepage_url}
                  </a>
                )}
                {user?.email && <p className="pp-email">{user.email}</p>}
              </>
            ) : (
              <div className="pp-empty-state">
                <FiUser className="pp-empty-icon" />
                <p>Set up your research identity — this pre-fills every proposal you write.</p>
                <button className="pp-btn pp-btn--primary" onClick={() => startEdit('identity')}>
                  <FiEdit2 /> Set up profile
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── B. Research Expertise ──────────────────────────────────────── */}
      <div className="pp-section">
        <div className="pp-section-header">
          <h2><FiBriefcase /> Research Expertise</h2>
          {editingSection !== 'expertise' && (
            <button className="pp-edit-btn" onClick={() => startEdit('expertise')}>
              <FiEdit2 /> Edit
            </button>
          )}
        </div>

        {editingSection === 'expertise' ? (
          <div className="pp-form">
            <div className="pp-form-group">
              <label>Describe your primary research areas and technical strengths</label>
              <textarea rows={5} value={form.expertise || ''} onChange={e => set('expertise', e.target.value)}
                placeholder="e.g., Transportation safety modeling, mixed logit models, crash severity analysis, connected vehicle systems..." />
            </div>
            <div className="pp-save-row">
              <button className="pp-btn pp-btn--primary" onClick={saveProfile} disabled={saving}>
                <FiSave /> {saving ? 'Saving...' : 'Save'}
              </button>
              <button className="pp-btn pp-btn--outline" onClick={cancelEdit}><FiX /> Cancel</button>
            </div>
          </div>
        ) : p.expertise ? (
          <p className="pp-text-content">{p.expertise}</p>
        ) : (
          <div className="pp-empty-state">
            <p>Describe your research areas and strengths. The AI uses this to tailor gap analyses and find collaborators.</p>
            <button className="pp-btn pp-btn--outline" onClick={() => startEdit('expertise')}><FiEdit2 /> Add expertise</button>
          </div>
        )}
      </div>

      {/* ── C. Grants & Proposals ────────────────────────────────────── */}
      <div className="pp-section">
        <div className="pp-section-header">
          <h2><FiAward /> Grants & Proposals</h2>
        </div>

        {/* Upload button */}
        <div className="pp-upload-section">
          <button className="pp-upload-btn" onClick={() => { setUploadForm({ files: [], outcome: 'funded', score: '', feedback: '', feedbackFile: null, nofoUrl: '', nofoFile: null }); setShowUploadModal(true); }} disabled={uploadingProposal}>
            <FiUpload /> {uploadingProposal ? 'Uploading...' : 'Upload a Proposal (PDF)'}
          </button>
          <span className="pp-upload-hint">Upload funded and rejected proposals with reviewer feedback to build your style fingerprint, win patterns, and reviewer critique analysis.</span>
        </div>

        {/* Upload modal */}
        {showUploadModal && (
          <div className="pp-modal-overlay" onClick={() => !uploadingProposal && setShowUploadModal(false)}>
            <div className="pp-modal" onClick={e => e.stopPropagation()}>
              <div className="pp-modal-header">
                <h3><FiUpload /> Upload Proposal</h3>
                <button className="pp-modal-close" onClick={() => !uploadingProposal && setShowUploadModal(false)}><FiX /></button>
              </div>
              <div className="pp-modal-body">
                <div className="pp-form-group">
                  <label>Proposal PDFs <span className="pp-required">*</span></label>
                  <input type="file" accept=".pdf" multiple
                    onChange={e => setUploadForm(f => ({ ...f, files: Array.from(e.target.files || []) }))} />
                  <span className="pp-hint-inline">Select one or more PDFs (e.g., cover sheet + summary + description + references)</span>
                  {uploadForm.files.length > 1 && (
                    <div className="pp-file-list">
                      {uploadForm.files.map((f, i) => <span key={i} className="pp-chip">{f.name}</span>)}
                    </div>
                  )}
                </div>
                <div className="pp-form-group">
                  <label>Outcome</label>
                  <select value={uploadForm.outcome}
                    onChange={e => setUploadForm(f => ({ ...f, outcome: e.target.value }))}>
                    <option value="funded">Funded</option>
                    <option value="rejected">Rejected</option>
                    <option value="pending">Pending</option>
                    <option value="withdrawn">Withdrawn</option>
                  </select>
                </div>
                <div className="pp-form-group">
                  <label>Priority Score <span className="pp-hint-inline">(NIH 1–9, lower is better — optional)</span></label>
                  <input type="number" min="1" max="9" placeholder="e.g., 3"
                    value={uploadForm.score}
                    onChange={e => setUploadForm(f => ({ ...f, score: e.target.value }))} />
                </div>
                <div className="pp-form-group">
                  <label>NOFO / RFP <span className="pp-hint-inline">(optional — PDF or URL)</span></label>
                  <input type="file" accept=".pdf"
                    onChange={e => setUploadForm(f => ({ ...f, nofoFile: e.target.files?.[0] || null, nofoUrl: '' }))} />
                  <span className="pp-or-divider">or paste URL:</span>
                  <input type="url" placeholder="https://grants.nih.gov/..."
                    value={uploadForm.nofoUrl}
                    onChange={e => setUploadForm(f => ({ ...f, nofoUrl: e.target.value, nofoFile: null }))} />
                </div>
                <div className="pp-form-group">
                  <label>Reviewer Feedback <span className="pp-hint-inline">(optional — paste text or upload PDF/DOCX)</span></label>
                  <input type="file" accept=".pdf,.docx"
                    onChange={e => setUploadForm(f => ({ ...f, feedbackFile: e.target.files?.[0] || null }))} />
                  <span className="pp-or-divider">or paste text:</span>
                  <textarea rows={4} placeholder="Paste reviewer comments, summary statement, panel review..."
                    value={uploadForm.feedback}
                    onChange={e => setUploadForm(f => ({ ...f, feedback: e.target.value }))} />
                </div>
                <div className="pp-form-group pp-form-group--corpus">
                  <label className="pp-corpus-checkbox">
                    <input
                      type="checkbox"
                      checked={uploadForm.contributeToCorpus}
                      onChange={e => {
                        const checked = e.target.checked;
                        if (checked && consentStatus !== 'granted') {
                          setShowConsentModal(true);
                          return;
                        }
                        setUploadForm(f => ({ ...f, contributeToCorpus: checked }));
                      }}
                    />
                    <span>
                      <strong>Contribute this submission to the reference corpus</strong>
                      <span className="pp-hint-inline">
                        De-identified and used only to improve reviewer quality.
                        {consentStatus !== 'granted' && ' You\'ll be asked for consent first.'}
                      </span>
                    </span>
                  </label>
                </div>
              </div>
              <div className="pp-modal-footer">
                <button className="pp-btn pp-btn--outline" onClick={() => setShowUploadModal(false)} disabled={uploadingProposal}>Cancel</button>
                <button className="pp-btn pp-btn--primary" onClick={submitProposalUpload}
                  disabled={!uploadForm.files.length || uploadingProposal}>
                  <FiUpload /> {uploadingProposal ? 'Parsing & Importing...' : 'Upload & Import'}
                </button>
              </div>
            </div>
          </div>
        )}

        <ConsentModal
          isOpen={showConsentModal}
          onClose={() => setShowConsentModal(false)}
          onDecision={(decision) => {
            setConsentStatus(decision);
            if (decision === 'granted') {
              setUploadForm(f => ({ ...f, contributeToCorpus: true }));
            }
          }}
        />

        {/* Unified grant list: uploaded proposals + unmatched imported grants */}
        {(() => {
          // Fuzzy title matching: extract significant words (3+ chars) and check overlap
          const getWords = (s) => (s || '').toLowerCase().replace(/[^a-z0-9\s]/g, '').split(/\s+/).filter(w => w.length >= 3);
          const titlesMatch = (a, b) => {
            const wa = getWords(a), wb = getWords(b);
            if (!wa.length || !wb.length) return false;
            const shared = wa.filter(w => wb.includes(w)).length;
            const minLen = Math.min(wa.length, wb.length);
            return shared >= Math.max(2, minLen * 0.4); // 40%+ word overlap or at least 2 shared words
          };
          // Imported grants that don't fuzzy-match any uploaded proposal
          const unmatchedImports = grantHistory.filter(g =>
            !uploadedProposals.some(u => titlesMatch(u.title, g.title))
          );
          const hasAny = uploadedProposals.length > 0 || unmatchedImports.length > 0;

          return hasAny ? (
            <div className="pp-proposal-cards">
              {/* Uploaded proposals — expandable rich cards */}
              {uploadedProposals.map(up => {
                const isExpanded = expandedProposal === up.id;
                const isEditingThis = editingTitle === up.id;
                return (
                  <div key={up.id} className={`pp-proposal-card pp-proposal-card--${up.funding_outcome || 'funded'} ${isExpanded ? 'pp-proposal-card--expanded' : ''}`}>
                    <div className="pp-proposal-card-top pp-proposal-card-top--clickable" onClick={() => setExpandedProposal(isExpanded ? null : up.id)}>
                      <span className="pp-proposal-card-chevron">
                        {isExpanded ? <FiChevronDown /> : <FiChevronRight />}
                      </span>
                      <div className="pp-proposal-card-title-block">
                        {isEditingThis ? (
                          <div className="pp-inline-edit" onClick={e => e.stopPropagation()}>
                            <input className="pp-inline-edit-input" value={editTitleValue}
                              onChange={e => setEditTitleValue(e.target.value)}
                              onKeyDown={async (e) => {
                                if (e.key === 'Enter' && editTitleValue.trim()) {
                                  await fetch(getApiEndpoint(`/api/grant-proposals/${up.id}/title`), {
                                    method: 'PATCH',
                                    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ title: editTitleValue.trim() }),
                                  });
                                  setEditingTitle(null);
                                  fetchData();
                                } else if (e.key === 'Escape') setEditingTitle(null);
                              }}
                              autoFocus />
                            <button className="pp-btn pp-btn--outline pp-btn--sm" onClick={async () => {
                              if (editTitleValue.trim()) {
                                await fetch(getApiEndpoint(`/api/grant-proposals/${up.id}/title`), {
                                  method: 'PATCH',
                                  headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ title: editTitleValue.trim() }),
                                });
                                setEditingTitle(null);
                                fetchData();
                              }
                            }}><FiSave /></button>
                            <button className="pp-btn pp-btn--outline pp-btn--sm" onClick={() => setEditingTitle(null)}><FiX /></button>
                          </div>
                        ) : (
                          <span className="pp-proposal-card-title">{up.title}</span>
                        )}
                        <div className="pp-proposal-card-meta">
                          <span className="pp-chip pp-chip--green">PDF uploaded</span>
                          <span className="pp-muted">{up.sections?.length || 0} sections</span>
                          {up.priority_score && <span className="pp-chip pp-chip--blue">Score: {up.priority_score}</span>}
                          {up.has_nofo && <span className="pp-chip pp-chip--teal">NOFO</span>}
                          {up.has_feedback && <span className="pp-chip pp-chip--purple">Feedback</span>}
                        </div>
                      </div>
                      <select
                        className="pp-outcome-select"
                        value={up.funding_outcome || 'funded'}
                        onClick={e => e.stopPropagation()}
                        onChange={async (e) => {
                          try {
                            await fetch(getApiEndpoint(`/api/grant-proposals/${up.id}/outcome`), {
                              method: 'PATCH',
                              headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
                              body: JSON.stringify({ funding_outcome: e.target.value }),
                            });
                            fetchData();
                          } catch (_) {}
                        }}
                      >
                        <option value="funded">Funded</option>
                        <option value="rejected">Rejected</option>
                        <option value="pending">Pending</option>
                        <option value="withdrawn">Withdrawn</option>
                      </select>
                    </div>
                    {isExpanded && (
                      <div className="pp-proposal-detail">
                        <div className="pp-proposal-detail-row">
                          <span className="pp-proposal-detail-label">Sections parsed:</span>
                          <div className="pp-proposal-detail-chips">
                            {(up.sections || []).map((s, i) => <span key={i} className="pp-chip">{s}</span>)}
                            {(!up.sections || !up.sections.length) && <span className="pp-muted">None</span>}
                          </div>
                        </div>
                        <div className="pp-proposal-detail-row">
                          <span className="pp-proposal-detail-label">NOFO/RFP:</span>
                          <span className={up.has_nofo ? '' : 'pp-muted'}>{up.has_nofo ? 'Attached' : 'Not provided'}</span>
                        </div>
                        <div className="pp-proposal-detail-row">
                          <span className="pp-proposal-detail-label">Reviewer feedback:</span>
                          <span className={up.has_feedback ? '' : 'pp-muted'}>{up.has_feedback ? 'Captured' : 'Not provided'}</span>
                        </div>
                        <div className="pp-proposal-detail-row">
                          <span className="pp-proposal-detail-label">Priority score:</span>
                          <span className={up.priority_score ? '' : 'pp-muted'}>{up.priority_score ? `${up.priority_score} / 9` : 'Not provided'}</span>
                        </div>
                        {up.created_at && (
                          <div className="pp-proposal-detail-row">
                            <span className="pp-proposal-detail-label">Uploaded:</span>
                            <span className="pp-muted">{new Date(up.created_at).toLocaleDateString()}</span>
                          </div>
                        )}
                        <div className="pp-proposal-detail-actions">
                          <button className="pp-btn pp-btn--outline pp-btn--sm" onClick={(e) => {
                            e.stopPropagation();
                            setEditTitleValue(up.title);
                            setEditingTitle(up.id);
                          }}><FiEdit2 /> Edit title</button>
                          <button className="pp-btn pp-btn--outline pp-btn--sm pp-btn--danger" onClick={async (e) => {
                            e.stopPropagation();
                            if (window.confirm(`Delete "${up.title}"? This cannot be undone.`)) {
                              await fetch(getApiEndpoint(`/api/grant-proposals/${up.id}`), {
                                method: 'DELETE', headers: getAuthHeaders(),
                              });
                              setExpandedProposal(null);
                              fetchData();
                            }
                          }}><FiTrash2 /> Delete</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Imported grants without uploaded PDF — dimmed with upload prompt */}
              {unmatchedImports.map((g, i) => {
                // Find the original index in grantHistory for deletion
                const origIdx = grantHistory.indexOf(g);
                return (
                  <div key={`imp-${i}`} className="pp-proposal-card pp-proposal-card--imported">
                    <div className="pp-proposal-card-top">
                      <div className="pp-proposal-card-title-block">
                        <span className="pp-proposal-card-title">{g.title}</span>
                        <div className="pp-proposal-card-meta">
                          {g.agency && <span className="pp-chip">{g.agency}</span>}
                          {g.mechanism && <span className="pp-chip">{g.mechanism}</span>}
                          {g.year && <span className="pp-muted">{g.year}</span>}
                          {g.status && <span className="pp-chip pp-chip--green">{g.status}</span>}
                        </div>
                      </div>
                      <div className="pp-proposal-card-actions">
                        <button
                          className="pp-btn pp-btn--outline pp-btn--sm"
                          onClick={() => {
                            setUploadForm({ files: [], outcome: g.status === 'funded' ? 'funded' : 'funded', score: '', feedback: '', feedbackFile: null, nofoUrl: '', nofoFile: null });
                            setShowUploadModal(true);
                          }}
                        >
                          <FiUpload /> Upload PDF
                        </button>
                        <button
                          className="pp-btn pp-btn--outline pp-btn--sm pp-btn--danger"
                          title="Remove this imported grant"
                          onClick={async () => {
                            if (origIdx >= 0) {
                              await fetch(getApiEndpoint(`/api/grant-proposals/imported-grant/${origIdx}`), {
                                method: 'DELETE', headers: getAuthHeaders(),
                              });
                              fetchData();
                            }
                          }}
                        ><FiTrash2 /></button>
                      </div>
                    </div>
                    <div className="pp-proposal-card-meta">
                      <span className="pp-muted">Detected from profile — upload the PDF to teach the system your writing style</span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : null;
        })()}

        {/* Freeform prior grants (edit mode) */}
        {editingSection !== 'grants' && (
          <button className="pp-edit-btn" style={{ marginTop: 10 }} onClick={() => startEdit('grants')}>
            <FiEdit2 /> Edit grant notes
          </button>
        )}
        {editingSection === 'grants' && (
          <div className="pp-form" style={{ marginTop: 12 }}>
            <div className="pp-form-group">
              <label>Additional grant notes (freeform)</label>
              <textarea rows={4} value={form.prior_grants || ''} onChange={e => set('prior_grants', e.target.value)}
                placeholder="e.g., NSF CMMI-2048329 (PI, $450K, 2021-2024): Connected vehicle safety..." />
            </div>
            <button className="pp-btn pp-btn--outline pp-fetch-btn" onClick={fetchGrants} disabled={fetchingGrants}>
              <FiDownloadCloud /> {fetchingGrants ? 'Fetching...' : 'Fetch from NIH / NSF / ORCID'}
            </button>
            <div className="pp-save-row">
              <button className="pp-btn pp-btn--primary" onClick={saveProfile} disabled={saving}>
                <FiSave /> {saving ? 'Saving...' : 'Save'}
              </button>
              <button className="pp-btn pp-btn--outline" onClick={cancelEdit}><FiX /> Cancel</button>
            </div>
          </div>
        )}
        {editingSection !== 'grants' && p.prior_grants && (
          <details className="pp-grants-text-toggle">
            <summary>Additional grant notes</summary>
            <p className="pp-text-content pp-text-pre">{p.prior_grants}</p>
          </details>
        )}

        {/* Empty state */}
        {!uploadedProposals.length && !grantHistory.length && !p.prior_grants && editingSection !== 'grants' && (
          <div className="pp-empty-state">
            <p>Upload proposals to build your funding history, style fingerprint, and win patterns.</p>
          </div>
        )}
      </div>

      {/* ── D. Team Assets ────────────────────────────────────────────── */}
      <div className="pp-section">
        <div className="pp-section-header">
          <h2><FiBriefcase /> Team Assets</h2>
          {editingSection !== 'assets' && (
            <button className="pp-edit-btn" onClick={() => startEdit('assets')}>
              <FiEdit2 /> Edit
            </button>
          )}
        </div>

        {editingSection === 'assets' ? (
          <div className="pp-form">
            <div className="pp-form-group">
              <label>Labs, testbeds, datasets, industry partnerships, equipment</label>
              <textarea rows={4} value={form.team_assets || ''} onChange={e => set('team_assets', e.target.value)}
                placeholder="e.g., Access to state DOT crash databases, 50-node connected vehicle testbed, partnership with local transit authority..." />
            </div>
            <div className="pp-save-row">
              <button className="pp-btn pp-btn--primary" onClick={saveProfile} disabled={saving}>
                <FiSave /> {saving ? 'Saving...' : 'Save'}
              </button>
              <button className="pp-btn pp-btn--outline" onClick={cancelEdit}><FiX /> Cancel</button>
            </div>
          </div>
        ) : p.team_assets ? (
          <p className="pp-text-content">{p.team_assets}</p>
        ) : (
          <div className="pp-empty-state">
            <p>Describe your lab's assets — testbeds, datasets, industry partnerships. The AI considers these for team composition.</p>
            <button className="pp-btn pp-btn--outline" onClick={() => startEdit('assets')}><FiEdit2 /> Add assets</button>
          </div>
        )}
      </div>

      {/* ── E. AI Insights ────────────────────────────────────────────── */}
      <div className="pp-section">
        <div className="pp-section-header">
          <h2><FiZap /> AI Insights</h2>
          <button className="pp-edit-btn" onClick={async () => {
            setMessage({ type: 'success', text: 'Refreshing AI insights... This may take 30-60 seconds.' });
            try {
              await fetch(getApiEndpoint('/api/grant-proposals/refresh-insights'), {
                method: 'POST', headers: getAuthHeaders(),
              });
              setTimeout(() => fetchData(), 30000);
              setTimeout(() => fetchData(), 60000);
            } catch (_) {}
          }}>
            <FiRefreshCw /> Refresh
          </button>
        </div>
        <p className="pp-section-sub">These are automatically extracted as you upload proposals. They compound over time.</p>

        {/* E1: Writing Style Fingerprint */}
        <div className={`pp-insight-card ${!styleFp ? 'pp-insight-card--empty' : ''}`}>
          <h4><FiFeather /> Writing Style Fingerprint</h4>
          {styleFp ? (
            <div className="pp-insight-body">
              <div className="pp-chip-row">
                {styleFp.framing && <span className="pp-chip">Framing: {styleFp.framing}</span>}
                {styleFp.tone && <span className="pp-chip">Tone: {styleFp.tone}</span>}
                {styleFp.aims_count && <span className="pp-chip">Avg aims: {styleFp.aims_count}</span>}
              </div>
              {styleFp.summary && <p className="pp-insight-summary">{styleFp.summary}</p>}
              {styleFp.signature_moves && styleFp.signature_moves.length > 0 && (
                <div className="pp-insight-detail">
                  <strong>Signature moves:</strong>
                  <ul>{styleFp.signature_moves.map((m, i) => <li key={i}>{m}</li>)}</ul>
                </div>
              )}
              {styleFp.proposals_analyzed && (
                <p className="pp-insight-meta">Based on {styleFp.proposals_analyzed} proposal{styleFp.proposals_analyzed !== 1 ? 's' : ''}</p>
              )}
            </div>
          ) : (
            <p className="pp-empty-hint">Complete 3+ grant proposals to see your writing style fingerprint. The AI analyzes your drafts to calibrate its voice to match yours.</p>
          )}
        </div>

        {/* E2: Win Patterns */}
        <div className={`pp-insight-card ${!winPatterns ? 'pp-insight-card--empty' : ''}`}>
          <h4><FiTrendingUp /> Win Patterns</h4>
          {winPatterns ? (
            <div className="pp-insight-body">
              <div className="pp-chip-row">
                <span className="pp-chip pp-chip--green">{winPatterns.funded_count || 0} funded</span>
                <span className="pp-chip pp-chip--red">{winPatterns.rejected_count || 0} rejected</span>
              </div>
              {winPatterns.summary && <p className="pp-insight-summary">{winPatterns.summary}</p>}
              {winPatterns.patterns && winPatterns.patterns.length > 0 && (
                <div className="pp-patterns-list">
                  {winPatterns.patterns.map((pat, i) => (
                    <div key={i} className="pp-pattern-row">
                      <strong>{pat.feature}</strong>
                      <div className="pp-pattern-compare">
                        <span className="pp-pattern-funded">Funded: {pat.funded_tendency}</span>
                        <span className="pp-pattern-rejected">Rejected: {pat.rejected_tendency}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <p className="pp-empty-hint">Record outcomes on 2+ funded and 2+ rejected proposals to see your win formula. The AI compares structural features that separate wins from losses.</p>
          )}
        </div>

        {/* E3: Recurring Reviewer Critiques */}
        <div className={`pp-insight-card ${critiques.length === 0 ? 'pp-insight-card--empty' : ''}`}>
          <h4><FiAlertCircle /> Recurring Reviewer Critiques</h4>
          {critiques.length > 0 ? (
            <div className="pp-insight-body">
              {critiques.map((c, i) => (
                <div key={i} className="pp-critique-row">
                  <div className="pp-critique-header">
                    <strong>{c.theme}</strong>
                    {c.frequency && <span className="pp-chip">{c.frequency}x</span>}
                  </div>
                  {c.description && <p className="pp-critique-desc">{c.description}</p>}
                  {c.remedy && <p className="pp-critique-remedy">Remedy: {c.remedy}</p>}
                </div>
              ))}
            </div>
          ) : (
            <p className="pp-empty-hint">Upload reviewer feedback on multiple proposals to see recurring critique patterns and how to pre-empt them.</p>
          )}
        </div>
      </div>

      {/* ── F. Collaborator Network ───────────────────────────────────── */}
      <div className="pp-section">
        <div className="pp-section-header">
          <h2><FiUsers /> Collaborator Network</h2>
        </div>

        {partners.length > 0 ? (
          <div className="pp-partners-list">
            {partners.map(pt => (
              <div key={pt.id} className="pp-partner-card">
                <div className="pp-partner-main">
                  <h4>{pt.contact_name} <span className="pp-partner-inst">@ {pt.institution}</span></h4>
                  {pt.contact_title && <span className="pp-partner-title">{pt.contact_title}</span>}
                  {pt.work_stream && <span className="pp-chip">{pt.work_stream}</span>}
                  {pt.fit_reason && <p className="pp-partner-fit">{pt.fit_reason}</p>}
                </div>
                <button className="pp-partner-delete" onClick={() => deletePartner(pt.id)} title="Remove collaborator">
                  <FiTrash2 />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="pp-empty-state">
            <p>Your collaborator network builds automatically as you confirm team suggestions during grant proposals. No partners saved yet.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PIProfile;
